import React, { useEffect, useState } from 'react';
import { getDashboard } from '../services/api';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

const fmt = n => new Intl.NumberFormat('fr-CI').format(Math.round(n || 0));
const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-CI', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';
const MOIS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const now = new Date();

// ── Icônes SVG ───────────────────────────────────────────────────────────────
const IconAlert = ({ size = 16, color = '#E65100' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0 }}>
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const IconClock = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
    style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }}>
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const IconClose = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const IconUser = ({ color }) => (
  <svg width={32} height={32} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const IconCalendar = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0 }}>
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const IconBriefcase = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0 }}>
    <rect x="2" y="7" width="20" height="14" rx="2"/>
    <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
    <line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/>
  </svg>
);

// ── Helpers ───────────────────────────────────────────────────────────────────
const joursRestants = (dateEcheance) =>
  Math.ceil((new Date(dateEcheance) - now) / (1000 * 60 * 60 * 24));

const urgenceColor = (jours) => {
  if (jours <= 14) return { bg: '#FFEBEE', border: '#e53935', text: '#b71c1c', badge: '#e53935', label: 'Critique' };
  if (jours <= 30) return { bg: '#FFF3E0', border: '#E65100', text: '#BF360C', badge: '#E65100', label: 'Urgent' };
  return               { bg: '#FFF8E1', border: '#F9A825', text: '#E65100',  badge: '#F9A825', label: 'À surveiller' };
};

// ── Modal détail contrat ──────────────────────────────────────────────────────
function ModalContrat({ contrat, onClose, navigate }) {
  if (!contrat) return null;
  const j  = joursRestants(contrat.date_fin);
  const cl = urgenceColor(j);

  // Barre de progression : 60 jours = 100%, 0 jour = 0%
  const pctRestant = Math.max(0, Math.min(100, Math.round((j / 60) * 100)));

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 12, width: 420, maxWidth: '95vw',
          boxShadow: '0 20px 60px rgba(0,0,0,0.22)',
          overflow: 'hidden', animation: 'fadeInUp .18s ease',
        }}
      >
        {/* ── En-tête colorée selon urgence ── */}
        <div style={{
          background: cl.bg, borderBottom: `3px solid ${cl.badge}`,
          padding: '18px 20px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: '#fff', border: `2px solid ${cl.badge}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <IconUser color={cl.badge} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, color: '#1a2332' }}>
                {contrat.employe_nom}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                <span style={{
                  background: cl.badge, color: '#fff', borderRadius: 4,
                  padding: '2px 8px', fontSize: 11, fontWeight: 700,
                }}>
                  {contrat.type_contrat}
                </span>
                <span style={{
                  background: '#fff', color: cl.text, border: `1px solid ${cl.badge}`,
                  borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700,
                }}>
                  <IconClock size={10} />{j}j — {cl.label}
                </span>
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#666', padding: 4, borderRadius: 4,
            display: 'flex', alignItems: 'center',
          }}>
            <IconClose />
          </button>
        </div>

        {/* ── Corps ── */}
        <div style={{ padding: '18px 20px' }}>

          {/* Barre de progression du temps restant */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#888', marginBottom: 5 }}>
              <span>Temps restant avant échéance</span>
              <span style={{ fontWeight: 700, color: cl.badge }}>{j} jour{j > 1 ? 's' : ''}</span>
            </div>
            <div style={{ background: '#f0f0f0', borderRadius: 6, height: 8, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 6,
                width: pctRestant + '%',
                background: cl.badge,
                transition: 'width .4s ease',
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#bbb', marginTop: 3 }}>
              <span>Aujourd'hui</span>
              <span>60 jours</span>
            </div>
          </div>

          {/* Détails du contrat */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '9px 12px', background: '#f7f9fc', borderRadius: 7,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#555', fontSize: 12 }}>
                <IconBriefcase /> Type de contrat
              </div>
              <span style={{ fontWeight: 700, fontSize: 13, color: '#1a2332' }}>{contrat.type_contrat}</span>
            </div>

            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '9px 12px', background: '#f7f9fc', borderRadius: 7,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#555', fontSize: 12 }}>
                <IconCalendar /> Date de début
              </div>
              <span style={{ fontWeight: 600, fontSize: 13, color: '#1a2332' }}>
                {fmtDate(contrat.date_debut)}
              </span>
            </div>

            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '9px 12px', background: cl.bg, borderRadius: 7,
              border: `1px solid ${cl.border}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: cl.text, fontSize: 12, fontWeight: 600 }}>
                <IconCalendar /> Date d'échéance
              </div>
              <span style={{ fontWeight: 800, fontSize: 13, color: cl.badge }}>
                {fmtDate(contrat.date_fin)}
              </span>
            </div>

            {contrat.departement && (
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '9px 12px', background: '#f7f9fc', borderRadius: 7,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#555', fontSize: 12 }}>
                  <IconBriefcase /> Département
                </div>
                <span style={{ fontWeight: 600, fontSize: 13, color: '#1a2332' }}>{contrat.departement}</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
            <button
              onClick={() => { onClose(); navigate('/employes', { state: { openId: contrat.employe_id } }); }}
              style={{
                flex: 1, padding: '9px 0', textAlign: 'center',
                background: '#1565C0', color: '#fff', borderRadius: 7,
                fontWeight: 700, fontSize: 12, border: 'none', cursor: 'pointer',
              }}
            >
              Voir la fiche employé
            </button>
            <button
              onClick={() => { onClose(); navigate('/employes', { state: { openId: contrat.employe_id, tab: 'contrats' } }); }}
              style={{
                flex: 1, padding: '9px 0', textAlign: 'center',
                background: cl.badge, color: '#fff', borderRadius: 7,
                fontWeight: 700, fontSize: 12, border: 'none', cursor: 'pointer',
              }}
            >
              Gérer le contrat
            </button>
          </div>

          <button onClick={onClose} style={{
            width: '100%', marginTop: 8, padding: '7px 0',
            background: 'none', border: '1px solid #ddd', borderRadius: 7,
            color: '#888', fontSize: 12, cursor: 'pointer',
          }}>
            Fermer
          </button>
        </div>
      </div>

      {/* Animation CSS */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────
export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false); // rechargement sans bloquer le rendu
  const [selectedProjet,  setSelectedProjet]  = useState('');
  const [selectedContrat, setSelectedContrat] = useState('');
  const [selectedBureau,  setSelectedBureau]  = useState('');
  const [contratModal, setContratModal]       = useState(null);
  const [dataDeparts,  setDataDeparts]         = useState(null);   // §Départs
  const [alerteTab,    setAlerteTab]          = useState('contrats'); // 'contrats'|'essais'|'documents'

  const isFirstLoad = React.useRef(true);

  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    const params = {
      mois:  now.getMonth() + 1,
      annee: now.getFullYear(),
    };
    if (selectedProjet)  params.projet_id    = selectedProjet;
    if (selectedContrat) params.type_contrat = selectedContrat;
    if (selectedBureau)  params.bureau_id    = selectedBureau;
    getDashboard(params)
      .then(r  => setData(r.data))
      .catch(() => {})
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, [selectedProjet, selectedContrat, selectedBureau]); // eslint-disable-line

  // Charger les données départs (indépendant des filtres)
  useEffect(() => {
    api.get('/dashboard/departs')
      .then(r => { if (r.data?.success) setDataDeparts(r.data); })
      .catch(() => {}); // non bloquant
  }, []); // eslint-disable-line

  if (loading) return <div className="empty"><span className="spinner spinner-dark" /></div>;
  if (!data)   return <div className="empty">Impossible de charger le tableau de bord.</div>;

  // ── Destructuration sécurisée ─────────────────────────────────────────────
  const kpi                      = (data.kpi && typeof data.kpi === 'object') ? data.kpi : data;
  const repartition_contrat      = Array.isArray(data.repartition_contrat)    ? data.repartition_contrat    : [];
  const repartition_dept         = Array.isArray(data.repartition_dept)       ? data.repartition_dept       : [];
  const conges_stats             = Array.isArray(data.conges_stats)           ? data.conges_stats           : [];
  const projets                  = Array.isArray(data.projets)                ? data.projets                : [];
  const bureaux                  = Array.isArray(data.bureaux)                ? data.bureaux                : [];
  const repartition_projet       = Array.isArray(data.repartition_projet)     ? data.repartition_projet     : [];
  const contrats_echeance_proche = Array.isArray(data.contrats_echeance_proche) ? data.contrats_echeance_proche : [];
  const essais_echeance_proche   = Array.isArray(data.essais_echeance_proche)   ? data.essais_echeance_proche   : [];
  const documents_expiration     = Array.isArray(data.documents_expiration)     ? data.documents_expiration     : [];
  const rg                       = Array.isArray(data.repartition_genre)        ? data.repartition_genre        : [];

  // ── Congés ────────────────────────────────────────────────────────────────
  const totalConges = conges_stats.reduce((s, c) => s + parseInt(c.total || 0), 0);
  const approuves   = conges_stats.find(c => c.statut === 'Approuvé');
  const tauxAppro   = totalConges
    ? Math.round((parseInt(approuves?.total || 0) / totalConges) * 100)
    : 0;

  // ── Contrats ──────────────────────────────────────────────────────────────
  const cdi      = parseInt(repartition_contrat.find(r => r.type_contrat === 'CDI')?.total        || 0);
  const cdd      = parseInt(repartition_contrat.find(r => r.type_contrat === 'CDD')?.total        || 0);
  const stage    = parseInt(repartition_contrat.find(r => r.type_contrat === 'Stage')?.total      || 0);
  const cons     = parseInt(repartition_contrat.find(r => r.type_contrat === 'Consultant')?.total || 0);
  const benevoles= parseInt(repartition_contrat.find(r => r.type_contrat === 'Bénévole')?.total    || 0);
  const cddStage = cdd + stage;

  // ── Genre ─────────────────────────────────────────────────────────────────
  const resolveGenre = (kpiKeys, genres) => {
    for (const k of kpiKeys) {
      const v = kpi ? kpi[k] : undefined;
      if (v !== undefined && v !== null && v !== '') return parseInt(v);
    }
    for (const g of genres) {
      const found = rg.find(x => x.genre === g || x.sexe === g);
      if (found) return parseInt(found.total || found.count || 0);
    }
    return null;
  };

  let hommes = resolveGenre(
    ['total_hommes', 'nb_hommes', 'hommes', 'male_count'],
    ['Masculin', 'M', 'Homme', 'H', 'male']
  );
  let femmes = resolveGenre(
    ['total_femmes', 'nb_femmes', 'femmes', 'female_count'],
    ['Féminin', 'Feminin', 'F', 'Femme', 'female']
  );

  const totalEff = parseInt(kpi?.total_employes || 0);
  if (hommes !== null && femmes === null) femmes = Math.max(0, totalEff - hommes);
  if (femmes !== null && hommes === null) hommes = Math.max(0, totalEff - femmes);
  const displayHommes = hommes ?? 0;
  const displayFemmes = femmes ?? 0;
  // Effectif total = Hommes + Femmes (plus fiable que total_employes selon les filtres)
  const displayTotal = displayHommes + displayFemmes || totalEff;

  // ── Taux de présence dynamique ────────────────────────────────────────────
  // = (total_employes - nb_absents_approuvés) / total_employes * 100
  const nbAbsentsApprouves = parseInt(conges_stats.find(c => c.statut === 'Approuvé')?.total || 0);
  const tauxPresence = totalEff > 0
    ? Math.min(100, Math.round(((totalEff - nbAbsentsApprouves) / totalEff) * 100))
    : 0;

  // ── Complétude des fiches signalétiques (vraie mesure, calculée backend) ──
  const tauxCompletude = parseFloat(kpi?.completude_fiches || 0);

  /* ── Couverture paie ──────────────────────────────────────────────────────
     §3.63 — Part de l'effectif ayant reçu un document de paie sur le mois.
     Un consultant, un bénévole ou un prestataire est réglé sur FACTURE et
     n'a jamais de bulletin : le calcul additionne donc bulletins et factures,
     sans quoi l'indicateur restait mécaniquement bas. */
  const couvertureDetail = kpi?.couverture_detail || null;
  const docsPaieEmis     = couvertureDetail
    ? Number(couvertureDetail.emis || 0)
    : Number(kpi?.bulletins_mois || 0) + Number(kpi?.factures_mois || 0);
  const tauxCouverturePaie = parseFloat(
    kpi?.couverture_paie ?? (totalEff > 0 ? (docsPaieEmis / totalEff) * 100 : 0)
  );

  // ── Taux de rotation ─────────────────────────────────────────────────────
  const rotationRaw        = data.taux_rotation && typeof data.taux_rotation === 'object' ? data.taux_rotation : null;
  const tauxRotation       = rotationRaw?.taux   != null ? parseFloat(rotationRaw.taux)   : null;
  const rotationEffMoyen   = rotationRaw?.effectif_moyen ?? null;
  const rotationDetailMotifs = Array.isArray(rotationRaw?.detail_motifs) ? rotationRaw.detail_motifs : [];

  // ── Ventilation projet / bailleur ────────────────────────────────────────
  const ventilation_projet_bailleur = Array.isArray(data.ventilation_projet_bailleur)
    ? data.ventilation_projet_bailleur
    : [];

  // §Départs — données enrichies
  const departs_prevus   = Array.isArray(dataDeparts?.departs_prevus)    ? dataDeparts.departs_prevus    : [];
  const departs_mensuel  = Array.isArray(dataDeparts?.departs_mensuel)   ? dataDeparts.departs_mensuel   : [];
  const departs_motif    = Array.isArray(dataDeparts?.departs_par_motif) ? dataDeparts.departs_par_motif : [];
  const nbDepartsPrevus  = departs_prevus.length;

  // §INDICATEURS — Nouveaux blocs format Excel
  const repartition_contrat_genre = Array.isArray(data.repartition_contrat_genre) ? data.repartition_contrat_genre : [];
  const repartition_categorie     = Array.isArray(data.repartition_categorie)     ? data.repartition_categorie     : [];
  const repartition_fonction      = Array.isArray(data.repartition_fonction)      ? data.repartition_fonction      : [];
  const indicateurs_bureau        = Array.isArray(data.indicateurs_bureau)        ? data.indicateurs_bureau        : [];
  const indicateurs_projet        = Array.isArray(data.indicateurs_projet)        ? data.indicateurs_projet        : [];
  /* §3.65 — Totaux DISTINCTS : la somme des lignes d'un tableau par projet
     dépasse l'effectif, puisqu'un agent réparti sur plusieurs projets figure
     dans plusieurs lignes. On affiche donc le nombre de personnes distinctes. */
  const projetTotaux      = data.projet_totaux      || null;
  const ventilationTotaux = data.ventilation_totaux || null;
  const totalEffectif             = indicateurs_bureau.reduce((s, b) => s + parseInt(b.total||0), 0) || displayTotal;
  const totalFemmesGlobal         = indicateurs_bureau.reduce((s, b) => s + parseInt(b.femmes||0), 0) || displayFemmes;
  const totalHommesGlobal         = indicateurs_bureau.reduce((s, b) => s + parseInt(b.hommes||0), 0) || displayHommes;

  // ── Alertes échéance ──────────────────────────────────────────────────────
  const alertesEcheance = contrats_echeance_proche.filter(c => {
    const j = joursRestants(c.date_fin);
    return j >= 0 && j <= 60;
  });
  const totalAlertes = alertesEcheance.length + essais_echeance_proche.length + documents_expiration.length;

  return (
    <>
      {/* ── Modal contrat ────────────────────────────────────────────────── */}
      <ModalContrat contrat={contratModal} onClose={() => setContratModal(null)} navigate={navigate} />

      {/* ── En-tête ──────────────────────────────────────────────────────── */}
      <div className="page-header">
        <div>
          <h2>Tableau de bord RH</h2>
          <p>Analyse consolidée — GFM3 | {MOIS[now.getMonth()]} {now.getFullYear()}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            value={selectedProjet}
            onChange={e => setSelectedProjet(e.target.value)}
            style={{ fontSize: 12, padding: '5px 10px', border: '1.5px solid #c8d4e0', borderRadius: 5 }}
          >
            <option value="">Tous les projets</option>
            {projets.map(p => (
              <option key={p.id} value={p.id}>{p.nom}</option>
            ))}
          </select>
          <select
            value={selectedBureau}
            onChange={e => setSelectedBureau(e.target.value)}
            style={{ fontSize: 12, padding: '5px 10px', border: '1.5px solid #c8d4e0', borderRadius: 5 }}
          >
            <option value="">Tous les bureaux</option>
            {bureaux.map(b => (
              <option key={b.id} value={b.id}>{b.nom}</option>
            ))}
          </select>
          <select
            value={selectedContrat}
            onChange={e => setSelectedContrat(e.target.value)}
            style={{ fontSize: 12, padding: '5px 10px', border: '1.5px solid #c8d4e0', borderRadius: 5 }}
          >
            <option value="">Tous les contrats</option>
            <option value="CDI">CDI</option>
            <option value="CDD">CDD</option>
            <option value="Stage">Stage</option>
            <option value="Consultant">Consultant</option>
          </select>
          {refreshing && (
            <span style={{ fontSize: 11, color: '#1565C0', display:'flex', alignItems:'center', gap:4 }}>
              <span className="spinner spinner-dark" style={{ width:12, height:12 }} /> Actualisation…
            </span>
          )}
          <button className="btn btn-blue btn-sm" onClick={() => window.print()}>Rapport PDF</button>
        </div>
      </div>

      {/* ── Bannière alertes — 3 catégories avec onglets ─────────────────── */}
      {totalAlertes > 0 && (
        <div style={{
          background: '#FFF3E0', border: '1.5px solid #E65100', borderRadius: 8,
          padding: '10px 14px', marginBottom: 12,
        }}>
          {/* Titre + onglets */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <IconAlert size={18} color="#E65100" />
            <span style={{ fontWeight: 700, fontSize: 13, color: '#BF360C' }}>
              {totalAlertes} alerte{totalAlertes > 1 ? 's' : ''} RH
            </span>
            <div style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
              {[
                { key: 'contrats',  label: `Contrats (${alertesEcheance.length})`,   show: alertesEcheance.length > 0 },
                { key: 'essais',    label: `Périodes d'essai (${essais_echeance_proche.length})`, show: essais_echeance_proche.length > 0 },
                { key: 'documents', label: `Documents (${documents_expiration.length})`, show: documents_expiration.length > 0 },
              ].filter(t => t.show).map(t => (
                <button key={t.key} onClick={() => setAlerteTab(t.key)} style={{
                  fontSize: 11, fontWeight: alerteTab === t.key ? 700 : 500,
                  padding: '2px 10px', borderRadius: 4, cursor: 'pointer',
                  background: alerteTab === t.key ? '#E65100' : 'transparent',
                  color: alerteTab === t.key ? '#fff' : '#BF360C',
                  border: `1px solid ${alerteTab === t.key ? '#E65100' : '#E65100'}`,
                }}>{t.label}</button>
              ))}
            </div>
          </div>

          {/* Onglet Contrats */}
          {alerteTab === 'contrats' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {alertesEcheance.map((c, i) => {
                const j  = joursRestants(c.date_fin);
                const cl = urgenceColor(j);
                return (
                  <div key={i} onClick={() => setContratModal(c)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, background: cl.bg,
                      border: `1px solid ${cl.border}`, borderRadius: 6, padding: '5px 12px',
                      fontSize: 12, cursor: 'pointer', transition: 'all .15s ease',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 4px 12px ${cl.border}55`; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)'; }}
                  >
                    <IconAlert size={13} color={cl.badge} />
                    <span style={{ fontWeight: 600, color: cl.text }}>{c.employe_nom || c.nom}</span>
                    <span style={{ color: '#555' }}>— {c.type_contrat}</span>
                    <span style={{ background: cl.badge, color: '#fff', borderRadius: 4, padding: '1px 6px', fontWeight: 700, fontSize: 11 }}>
                      <IconClock size={11} />{j}j
                    </span>
                    <span style={{ color: cl.badge, fontSize: 11, marginLeft: 2 }}>›</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Onglet Périodes d'essai */}
          {alerteTab === 'essais' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {essais_echeance_proche.map((e, i) => {
                const j  = joursRestants(e.date_fin_essai);
                const cl = urgenceColor(j);
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, background: cl.bg,
                    border: `1px solid ${cl.border}`, borderRadius: 6, padding: '5px 12px', fontSize: 12 }}>
                    <IconAlert size={13} color={cl.badge} />
                    <span style={{ fontWeight: 600, color: cl.text }}>{e.employe_nom}</span>
                    <span style={{ color: '#555' }}>— {e.type_contrat}</span>
                    <span style={{ color: '#555', fontSize: 11 }}>{e.matricule}</span>
                    <span style={{ background: cl.badge, color: '#fff', borderRadius: 4, padding: '1px 6px', fontWeight: 700, fontSize: 11 }}>
                      <IconClock size={11} />{j}j
                    </span>
                    <button onClick={() => navigate('/employes', { state: { openId: e.employe_id } })}
                      style={{ fontSize: 11, color: '#1565C0', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                      Voir →
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Onglet Documents */}
          {alerteTab === 'documents' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {documents_expiration.map((d, i) => {
                const j  = joursRestants(d.date_expiration);
                const cl = urgenceColor(j);
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, background: cl.bg,
                    border: `1px solid ${cl.border}`, borderRadius: 6, padding: '5px 12px', fontSize: 12 }}>
                    <IconAlert size={13} color={cl.badge} />
                    <span style={{ fontWeight: 600, color: cl.text }}>{d.prenoms} {d.nom}</span>
                    <span style={{ color: '#555' }}>— {d.type_document}</span>
                    <span style={{ fontStyle: 'italic', color: '#777', fontSize: 11 }}>{d.intitule}</span>
                    <span style={{ background: cl.badge, color: '#fff', borderRadius: 4, padding: '1px 6px', fontWeight: 700, fontSize: 11 }}>
                      <IconClock size={11} />{j}j
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── KPI Row 1 ────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 10, marginBottom: 12 }}>
        <div className="metric">
          <div className="metric-label">Effectif total</div>
          <div className="metric-value mv-blue">{displayTotal}</div>
        </div>
        <div className="metric">
          <div className="metric-label">Hommes</div>
          <div className="metric-value mv-blue">{displayHommes}</div>
        </div>
        <div className="metric">
          <div className="metric-label">Femmes</div>
          <div className="metric-value mv-blue">{displayFemmes}</div>
        </div>
        <div className="metric">
          <div className="metric-label">CDI</div>
          <div className="metric-value mv-green">{cdi}</div>
        </div>
        <div className="metric">
          <div className="metric-label">CDD / Stage</div>
          <div className="metric-value mv-orange">{cddStage}</div>
        </div>
        <div className="metric">
          <div className="metric-label">Consultants</div>
          <div className="metric-value mv-blue">{cons}</div>
        </div>
        <div className="metric">
          <div className="metric-label">Bénévoles</div>
          <div className="metric-value mv-purple">{benevoles}</div>
        </div>
      </div>

      {/* ── KPI Row 2 ────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 10, marginBottom: 14 }}>
        <div className="metric">
          <div className="metric-label">Masse salariale (FCFA)</div>
          <div className="metric-value mv-green" style={{ fontSize: 18 }}>{fmt(kpi?.masse_salariale)}</div>
        </div>
        <div className="metric">
          <div className="metric-label">Coût moyen / agent</div>
          <div className="metric-value mv-green" style={{ fontSize: 18 }}>
            {totalEff > 0 ? fmt(kpi.masse_salariale / totalEff) : '0'}
          </div>
          {tauxCouverturePaie < 50 && (
            <div style={{ fontSize: 10, color: '#E65100', marginTop: 2 }}>
              ⚠ {kpi?.bulletins_mois ?? 0}/{totalEff} bulletins saisis
            </div>
          )}
        </div>
        <div className="metric">
          <div className="metric-label">Couverture paie</div>
          <div className="metric-value" style={{ fontSize: 18, color: tauxCouverturePaie < 50 ? '#e53935' : '#2E7D32' }}>
            {tauxCouverturePaie.toFixed(0)}%
          </div>
          {couvertureDetail && (
            <div style={{ fontSize: 10, color: '#888', marginTop: 2, lineHeight: 1.4 }}>
              {couvertureDetail.bulletins} bulletin{couvertureDetail.bulletins > 1 ? 's' : ''}
              {' + '}{couvertureDetail.factures} facture{couvertureDetail.factures > 1 ? 's' : ''}
              {' sur '}{couvertureDetail.attendus} agents
            </div>
          )}
        </div>
        <div className="metric">
          <div className="metric-label">Absences ce mois</div>
          <div className="metric-value mv-orange">{totalConges}</div>
        </div>
        <div className="metric">
          <div className="metric-label">Congés en attente</div>
          <div className="metric-value mv-orange">{kpi?.conges_en_attente ?? 0}</div>
        </div>
        <div className="metric">
          <div className="metric-label">Taux de rotation</div>
          <div className="metric-value mv-red">
            {tauxRotation !== null ? tauxRotation + '%' : <span style={{ fontSize: 13, color: '#aaa' }}>N/A</span>}
          </div>
        </div>
        <div className="metric" style={{ borderTop: nbDepartsPrevus > 0 ? '3px solid #f59e0b' : undefined }}>
          <div className="metric-label">Départs prévus (30j)</div>
          <div className="metric-value" style={{ color: nbDepartsPrevus > 0 ? '#c2410c' : '#aaa', fontSize: nbDepartsPrevus > 0 ? 28 : 20 }}>
            {nbDepartsPrevus > 0 ? nbDepartsPrevus : '—'}
          </div>
        </div>
      </div>

      {/* ── Graphes Row 1 ────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>

        <div className="card" style={{ padding: 14 }}>
          <div className="card-title">Absences par statut</div>
          {conges_stats.length > 0 ? conges_stats.map((c, i) => {
            const tot  = conges_stats.reduce((s, x) => s + parseInt(x.total || 0), 0);
            const pct  = tot ? Math.round(parseInt(c.total || 0) / tot * 100) : 0;
            const cols = { Approuvé: '#4CAF50', Rejeté: '#e53935', 'En attente': '#FF9800' };
            return (
              <div key={i} style={{ marginBottom: 8 }}>
                <div className="progress-label">
                  <span>{c.statut}</span>
                  <span style={{ fontWeight: 700 }}>{c.total} ({pct}%)</span>
                </div>
                <div className="perf-bar-wrap">
                  <div className="perf-bar" style={{ width: pct + '%', background: cols[c.statut] || '#999' }} />
                </div>
              </div>
            );
          }) : <div className="empty" style={{ padding: '1rem' }}>Aucune donnée</div>}
        </div>

        <div className="card" style={{ padding: 14 }}>
          <div className="card-title">Statistiques RH</div>
          {[
            { label: 'Taux approbation congés',  val: tauxAppro,     color: '#4CAF50' },
            { label: 'Taux de présence moyen',    val: tauxPresence,  color: '#1565C0' },
            { label: 'Complétude des fiches',     val: tauxCompletude,color: '#FF9800' },
            { label: 'Bulletins générés ce mois', val: (kpi?.bulletins_mois > 0 && kpi?.total_employes > 0)
                ? Math.min(100, Math.round(kpi.bulletins_mois / kpi.total_employes * 100))
                : 0,
              color: '#e53935' },
          ].map((p, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <div className="progress-label">
                <span style={{ color: '#555' }}>{p.label}</span>
                <span style={{ fontWeight: 700, color: p.color }}>{p.val}%</span>
              </div>
              <div className="perf-bar-wrap">
                <div className="perf-bar" style={{ width: p.val + '%', background: p.color }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Graphes Row 2 ────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>

        <div className="card" style={{ padding: 14 }}>
          <div className="card-title">Effectif par département</div>
          {repartition_dept.length > 0 ? repartition_dept.slice(0, 8).map((d, i) => {
            const max = Math.max(...repartition_dept.map(x => parseInt(x.total || 0)));
            const pct = max ? Math.round(parseInt(d.total || 0) / max * 100) : 0;
            return (
              <div key={i} style={{ marginBottom: 6 }}>
                <div className="progress-label">
                  <span style={{ fontSize: 11 }}>{d.nom}</span>
                  <span style={{ fontWeight: 700, fontSize: 11 }}>{d.total}</span>
                </div>
                <div className="perf-bar-wrap">
                  <div className="perf-bar" style={{ width: pct + '%', background: '#1565C0' }} />
                </div>
              </div>
            );
          }) : <div className="empty" style={{ padding: '1rem' }}>Aucune donnée</div>}
        </div>

        <div className="card">
          <div className="card-title">Suivi des absences & mouvements récents</div>
          <table>
            <thead>
              <tr><th>Statut</th><th style={{ textAlign: 'center' }}>Nombre</th><th style={{ textAlign: 'right' }}>% du total</th></tr>
            </thead>
            <tbody>
              {conges_stats.length > 0 ? conges_stats.map((c, i) => {
                const totC = conges_stats.reduce((s, x) => s + parseInt(x.total || 0), 0);
                const pct  = totC ? Math.round(parseInt(c.total || 0) / totC * 100) : 0;
                const colMap = { Approuvé: 'badge-green', Rejeté: 'badge-red', 'En attente': 'badge-orange', 'Validé': 'badge-blue' };
                return (
                  <tr key={i}>
                    <td><span className={`badge ${colMap[c.statut] || 'badge-gray'}`}>{c.statut}</span></td>
                    <td style={{ textAlign: 'center', fontWeight: 700 }}>{c.total}</td>
                    <td style={{ textAlign: 'right', color: '#888', fontSize: 12 }}>{pct}%</td>
                  </tr>
                );
              }) : (
                <tr><td colSpan={3} className="empty">Aucune donnée de congés</td></tr>
              )}
              {essais_echeance_proche.length > 0 && (
                <tr style={{ background: '#FFF3E0' }}>
                  <td><span className="badge badge-orange">Essais à confirmer</span></td>
                  <td style={{ textAlign: 'center', fontWeight: 700 }}>{essais_echeance_proche.length}</td>
                  <td style={{ textAlign: 'right', fontSize: 11, color: '#E65100' }}>dans ≤{Math.max(...essais_echeance_proche.map(e => e.jours_restants || 0))}j</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Section Départs ──────────────────────────────────────────────── */}
      {(departs_prevus.length > 0 || departs_motif.filter(d => d.nb_departs > 0).length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>

          {/* Départs prévus dans les 30 jours */}
          <div className="card" style={{ padding: 14 }}>
            <div className="card-title" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span>🚪 Départs prévus — 30 jours</span>
              {departs_prevus.length > 0 && (
                <span style={{ background:'#f59e0b', color:'#fff', borderRadius:20,
                  padding:'2px 10px', fontSize:11, fontWeight:700 }}>
                  {departs_prevus.length}
                </span>
              )}
            </div>
            {departs_prevus.length === 0 ? (
              <div className="empty" style={{ padding:'1rem' }}>Aucun départ prévu</div>
            ) : (
              <div style={{ overflowX:'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Département</th>
                      <th style={{ textAlign:'center' }}>Date</th>
                      <th style={{ textAlign:'center' }}>J-</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departs_prevus.map((d, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight:600, fontSize:12 }}>{d.nom_complet}</td>
                        <td style={{ fontSize:11, color:'#888' }}>{d.departement || '—'}</td>
                        <td style={{ textAlign:'center', fontFamily:'monospace', fontSize:11 }}>{d.date_depart}</td>
                        <td style={{ textAlign:'center' }}>
                          <span style={{
                            background: d.jours_restants <= 7 ? '#fee2e2' : '#fff7ed',
                            color: d.jours_restants <= 7 ? '#991b1b' : '#c2410c',
                            fontWeight:700, fontSize:11, padding:'2px 8px', borderRadius:20,
                          }}>
                            {d.jours_restants}j
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Évolution des départs 12 mois */}
          <div className="card" style={{ padding: 14 }}>
            <div className="card-title">📊 Départs par motif</div>
            {departs_motif.filter(d => d.nb_departs > 0).length === 0 ? (
              <div className="empty" style={{ padding:'1rem' }}>Aucun départ enregistré</div>
            ) : (
              departs_motif.filter(d => d.nb_departs > 0).map((d, i) => {
                const maxD = Math.max(...departs_motif.map(x => x.nb_departs || 0), 1);
                const pct  = Math.round((d.nb_departs / maxD) * 100);
                const colors = ['#e53935','#E65100','#6A1B9A','#00838F','#1565C0','#2E7D32'];
                return (
                  <div key={i} style={{ marginBottom:8 }}>
                    <div className="progress-label">
                      <span style={{ fontSize:11 }}>{d.motif}</span>
                      <span style={{ fontWeight:700, fontSize:11 }}>{d.nb_departs}</span>
                    </div>
                    <div className="perf-bar-wrap">
                      <div className="perf-bar" style={{ width: pct+'%', background: colors[i % colors.length] }} />
                    </div>
                  </div>
                );
              })
            )}

            {/* Mini graphe évolution mensuelle */}
            {departs_mensuel.length > 0 && (
              <div style={{ marginTop:14 }}>
                <div style={{ fontSize:11, fontWeight:600, color:'#888', marginBottom:8 }}>
                  Évolution mensuelle (12 mois)
                </div>
                <div style={{ display:'flex', alignItems:'flex-end', gap:4, height:50 }}>
                  {departs_mensuel.map((d, i) => {
                    const maxM = Math.max(...departs_mensuel.map(x => x.nb_departs || 0), 1);
                    return (
                      <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
                        <span style={{ fontSize:9, color:'#888' }}>{d.nb_departs || 0}</span>
                        <div style={{
                          width:'100%', background:'#e53935', borderRadius:'3px 3px 0 0',
                          height: `${((d.nb_departs || 0) / maxM) * 40}px`, minHeight: d.nb_departs ? 3 : 0,
                        }} />
                        <span style={{ fontSize:8, color:'#aaa', transform:'rotate(-45deg)', marginTop:2 }}>
                          {(d.mois || '').slice(5)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
           §INDICATEURS RH — Tableau global (format feuille Excel)
           ══════════════════════════════════════════════════════════════════ */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12,
          padding:'8px 14px', background:'#1565C0', borderRadius:8 }}>
          <span style={{ color:'#fff', fontWeight:800, fontSize:14 }}>📊 INDICATEURS RH</span>
          <span style={{ color:'#bbdefb', fontSize:12 }}>— Vue consolidée GFM3</span>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14, marginBottom:14 }}>

          {/* Tableau global effectifs */}
          <div className="card" style={{ padding:14 }}>
            <div className="card-title" style={{ borderBottom:'2px solid #1565C0', paddingBottom:6, marginBottom:10 }}>
              Tableau global des effectifs
            </div>
            {[
              { label:'Nombre actuel de staff', val: totalEffectif, color:'#1565C0', bold:true },
              { label:'Nombre actuel de femmes', val: `${totalFemmesGlobal} (${totalEffectif ? Math.round(totalFemmesGlobal/totalEffectif*100) : 0}%)`, color:'#e91e8c' },
              { label:"Nombre actuel d'hommes",  val: `${totalHommesGlobal} (${totalEffectif ? Math.round(totalHommesGlobal/totalEffectif*100) : 0}%)`, color:'#1565C0' },
            ].map((r,i) => (
              <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                padding:'7px 10px', background: i===0 ? '#e3f2fd' : '#f9fafb',
                borderRadius:6, marginBottom:5, borderLeft: i===0 ? '3px solid #1565C0' : 'none' }}>
                <span style={{ fontSize:12, color:'#555' }}>{r.label}</span>
                <strong style={{ color:r.color, fontSize:13 }}>{r.val}</strong>
              </div>
            ))}
          </div>

          {/* Effectifs par type de contrat avec genre */}
          <div className="card" style={{ padding:14 }}>
            <div className="card-title" style={{ borderBottom:'2px solid #4CAF50', paddingBottom:6, marginBottom:10 }}>
              Effectifs par type de contrat
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 60px 40px 40px 40px', gap:'4px 8px',
              fontSize:11, color:'#888', marginBottom:6, fontWeight:600 }}>
              <span>Type</span><span style={{ textAlign:'right' }}>Total</span>
              <span style={{ textAlign:'right', color:'#e91e8c' }}>♀</span>
              <span style={{ textAlign:'right', color:'#1565C0' }}>♂</span>
              <span style={{ textAlign:'right' }}>%</span>
            </div>
            {repartition_contrat_genre.map((r,i) => {
              const totT = repartition_contrat_genre.reduce((s,x) => s + parseInt(x.total||0), 0);
              const pct  = totT ? Math.round(parseInt(r.total||0) / totT * 100) : 0;
              return (
              <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 60px 40px 40px 40px',
                gap:'4px 8px', padding:'5px 0', borderBottom:'1px solid #f3f4f6', fontSize:12 }}>
                <span style={{ fontWeight:500 }}>{r.type_contrat}</span>
                <strong style={{ textAlign:'right', color:'#1565C0' }}>{r.total}</strong>
                <span style={{ textAlign:'right', color:'#e91e8c' }}>{r.femmes}</span>
                <span style={{ textAlign:'right', color:'#1565C0' }}>{r.hommes}</span>
                <span style={{ textAlign:'right', color:'#888', fontSize:11 }}>{pct}%</span>
              </div>
              );
            })}
            {repartition_contrat_genre.length > 0 && (() => {
              const tT = repartition_contrat_genre.reduce((s,r) => s + parseInt(r.total||0), 0);
              const tF = repartition_contrat_genre.reduce((s,r) => s + parseInt(r.femmes||0), 0);
              const tH = repartition_contrat_genre.reduce((s,r) => s + parseInt(r.hommes||0), 0);
              return (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 60px 40px 40px 40px',
                  gap:'4px 8px', padding:'6px 0 2px', borderTop:'2px solid #4CAF50',
                  marginTop:2, fontSize:12, background:'#f1f8e9', borderRadius:'0 0 6px 6px' }}>
                  <span style={{ fontWeight:700, color:'#2E7D32' }}>Total</span>
                  <strong style={{ textAlign:'right', color:'#2E7D32' }}>{tT}</strong>
                  <span style={{ textAlign:'right', color:'#e91e8c', fontWeight:700 }}>{tF}</span>
                  <span style={{ textAlign:'right', color:'#1565C0', fontWeight:700 }}>{tH}</span>
                  <span style={{ textAlign:'right', color:'#888', fontSize:11 }}>100%</span>
                </div>
              );
            })()}
          </div>

          {/* Effectifs par catégorie professionnelle */}
          <div className="card" style={{ padding:14 }}>
            <div className="card-title" style={{ borderBottom:'2px solid #FF9800', paddingBottom:6, marginBottom:10 }}>
              Effectifs par catégorie professionnelle
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 60px 40px 40px 40px', gap:'4px 8px',
              fontSize:11, color:'#888', marginBottom:6, fontWeight:600 }}>
              <span>Catégorie</span><span style={{ textAlign:'right' }}>Total</span>
              <span style={{ textAlign:'right', color:'#e91e8c' }}>♀</span>
              <span style={{ textAlign:'right', color:'#1565C0' }}>♂</span>
              <span style={{ textAlign:'right' }}>%</span>
            </div>
            {repartition_categorie.map((r,i) => {
              const totT = repartition_categorie.reduce((s,x) => s + parseInt(x.total||0), 0);
              const pct  = totT ? Math.round(parseInt(r.total||0) / totT * 100) : 0;
              return (
              <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 60px 40px 40px 40px',
                gap:'4px 8px', padding:'5px 0', borderBottom:'1px solid #f3f4f6', fontSize:12 }}>
                <span style={{ fontWeight:500 }}>{r.categorie}</span>
                <strong style={{ textAlign:'right', color:'#FF9800' }}>{r.total}</strong>
                <span style={{ textAlign:'right', color:'#e91e8c' }}>{r.femmes}</span>
                <span style={{ textAlign:'right', color:'#1565C0' }}>{r.hommes}</span>
                <span style={{ textAlign:'right', color:'#888', fontSize:11 }}>{pct}%</span>
              </div>
              );
            })}
            {repartition_categorie.length > 0 && (() => {
              const tT = repartition_categorie.reduce((s,r) => s + parseInt(r.total||0), 0);
              const tF = repartition_categorie.reduce((s,r) => s + parseInt(r.femmes||0), 0);
              const tH = repartition_categorie.reduce((s,r) => s + parseInt(r.hommes||0), 0);
              return (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 60px 40px 40px 40px',
                  gap:'4px 8px', padding:'6px 0 2px', borderTop:'2px solid #FF9800',
                  marginTop:2, fontSize:12, background:'#fff8e1', borderRadius:'0 0 6px 6px' }}>
                  <span style={{ fontWeight:700, color:'#E65100' }}>Total</span>
                  <strong style={{ textAlign:'right', color:'#E65100' }}>{tT}</strong>
                  <span style={{ textAlign:'right', color:'#e91e8c', fontWeight:700 }}>{tF}</span>
                  <span style={{ textAlign:'right', color:'#1565C0', fontWeight:700 }}>{tH}</span>
                  <span style={{ textAlign:'right', color:'#888', fontSize:11 }}>100%</span>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Répartition par fonction */}
        <div style={{ marginBottom:14 }}>
          <div className="card" style={{ padding:14 }}>
            <div className="card-title" style={{ borderBottom:'2px solid #00838F', paddingBottom:6, marginBottom:10 }}>
              Répartition par fonction
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 60px 40px 40px 40px', gap:'4px 8px',
              fontSize:11, color:'#888', marginBottom:6, fontWeight:600 }}>
              <span>Fonction</span>
              <span style={{ textAlign:'right' }}>Total</span>
              <span style={{ textAlign:'right', color:'#e91e8c' }}>♀</span>
              <span style={{ textAlign:'right', color:'#1565C0' }}>♂</span>
              <span style={{ textAlign:'right' }}>%</span>
            </div>
            {(() => {
              const totF = repartition_fonction.reduce((s,r) => s + parseInt(r.total||0), 0);
              return repartition_fonction.map((r,i) => {
                const pct = totF ? Math.round(parseInt(r.total||0) / totF * 100) : 0;
                return (
                  <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 60px 40px 40px 40px',
                    gap:'4px 8px', padding:'5px 0', borderBottom:'1px solid #f3f4f6', fontSize:12,
                    background: i%2===0 ? '#fff' : '#f9fafb' }}>
                    <span style={{ fontWeight:500 }}>{r.fonction}</span>
                    <strong style={{ textAlign:'right', color:'#00838F' }}>{r.total}</strong>
                    <span style={{ textAlign:'right', color:'#e91e8c' }}>{r.femmes}</span>
                    <span style={{ textAlign:'right', color:'#1565C0' }}>{r.hommes}</span>
                    <span style={{ textAlign:'right', color:'#888', fontSize:11 }}>{pct}%</span>
                  </div>
                );
              });
            })()}
            {repartition_fonction.length > 0 && (() => {
              const tT = repartition_fonction.reduce((s,r) => s + parseInt(r.total||0), 0);
              const tF = repartition_fonction.reduce((s,r) => s + parseInt(r.femmes||0), 0);
              const tH = repartition_fonction.reduce((s,r) => s + parseInt(r.hommes||0), 0);
              return (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 60px 40px 40px 40px',
                  gap:'4px 8px', padding:'6px 0 2px', borderTop:'2px solid #00838F',
                  marginTop:2, fontSize:12, background:'#e0f7fa', borderRadius:'0 0 6px 6px' }}>
                  <span style={{ fontWeight:700, color:'#00838F' }}>Total</span>
                  <strong style={{ textAlign:'right', color:'#00838F' }}>{tT}</strong>
                  <span style={{ textAlign:'right', color:'#e91e8c', fontWeight:700 }}>{tF}</span>
                  <span style={{ textAlign:'right', color:'#1565C0', fontWeight:700 }}>{tH}</span>
                  <span style={{ textAlign:'right', color:'#888', fontSize:11 }}>100%</span>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Effectifs par bureau — tableau complet avec genre */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
          <div className="card" style={{ padding:14 }}>
            <div className="card-title" style={{ borderBottom:'2px solid #00838F', paddingBottom:6, marginBottom:10 }}>
              Effectifs par bureaux
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ background:'#e0f7fa' }}>
                  <th style={{ padding:'6px 8px', textAlign:'left', fontWeight:700, color:'#00838F' }}>Bureau</th>
                  <th style={{ padding:'6px 8px', textAlign:'right', fontWeight:700 }}>Total</th>
                  <th style={{ padding:'6px 8px', textAlign:'right', fontWeight:700, color:'#e91e8c' }}>♀</th>
                  <th style={{ padding:'6px 8px', textAlign:'right', fontWeight:700, color:'#1565C0' }}>♂</th>
                  <th style={{ padding:'6px 8px', textAlign:'right', fontWeight:700, color:'#888' }}>%</th>
                </tr>
              </thead>
              <tbody>
                {indicateurs_bureau.filter(b => parseInt(b.total||0) > 0).map((b,i) => (
                  <tr key={i} style={{ borderBottom:'1px solid #f0f0f0',
                    background: i%2===0 ? '#fafafa' : '#fff' }}>
                    <td style={{ padding:'5px 8px', fontWeight:600 }}>{b.ville || b.bureau}</td>
                    <td style={{ padding:'5px 8px', textAlign:'right', fontWeight:700, color:'#00838F' }}>{b.total}</td>
                    <td style={{ padding:'5px 8px', textAlign:'right', color:'#e91e8c' }}>{b.femmes}</td>
                    <td style={{ padding:'5px 8px', textAlign:'right', color:'#1565C0' }}>{b.hommes}</td>
                    <td style={{ padding:'5px 8px', textAlign:'right', color:'#888', fontSize:11 }}>{b.pct}%</td>
                  </tr>
                ))}
                {indicateurs_bureau.length > 0 && (
                  <tr style={{ background:'#e0f7fa', borderTop:'2px solid #00838F', fontWeight:700 }}>
                    <td style={{ padding:'6px 8px', color:'#00838F' }}>Total</td>
                    <td style={{ padding:'6px 8px', textAlign:'right', color:'#00838F' }}>
                      {indicateurs_bureau.reduce((s,b) => s + parseInt(b.total||0), 0)}
                    </td>
                    <td style={{ padding:'6px 8px', textAlign:'right', color:'#e91e8c' }}>
                      {indicateurs_bureau.reduce((s,b) => s + parseInt(b.femmes||0), 0)}
                    </td>
                    <td style={{ padding:'6px 8px', textAlign:'right', color:'#1565C0' }}>
                      {indicateurs_bureau.reduce((s,b) => s + parseInt(b.hommes||0), 0)}
                    </td>
                    <td style={{ padding:'6px 8px', textAlign:'right', color:'#888', fontSize:11 }}>100%</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Effectifs par projet avec genre */}
          <div className="card" style={{ padding:14 }}>
            <div className="card-title" style={{ borderBottom:'2px solid #6A1B9A', paddingBottom:6, marginBottom:10 }}>
              Effectifs par programmes / Projets
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ background:'#f3e5f5' }}>
                  <th style={{ padding:'6px 8px', textAlign:'left', fontWeight:700, color:'#6A1B9A' }}>Projet</th>
                  <th style={{ padding:'6px 8px', textAlign:'left', fontWeight:700, color:'#888', fontSize:10 }}>Bailleur</th>
                  <th style={{ padding:'6px 8px', textAlign:'right', fontWeight:700 }}>Agents</th>
                  <th style={{ padding:'6px 8px', textAlign:'right', fontWeight:700, color:'#00897B' }}
                      title="Équivalent temps plein : somme des taux d'intervention. Le total ETP égale l'effectif réel.">ETP</th>
                  <th style={{ padding:'6px 8px', textAlign:'right', fontWeight:700, color:'#e91e8c' }}>♀</th>
                  <th style={{ padding:'6px 8px', textAlign:'right', fontWeight:700, color:'#1565C0' }}>♂</th>
                  <th style={{ padding:'6px 8px', textAlign:'right', fontWeight:700, color:'#888' }}>% ETP</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const totEtp = indicateurs_projet.reduce((s,p) => s + parseFloat(p.etp||0), 0);
                  return indicateurs_projet.filter(p => parseInt(p.total||0) > 0).map((p,i) => {
                    const pct = totEtp ? Math.round(parseFloat(p.etp||0) / totEtp * 100) : 0;
                    return (
                      <tr key={i} style={{ borderBottom:'1px solid #f0f0f0',
                        background: i%2===0 ? '#fafafa' : '#fff' }}>
                        <td style={{ padding:'5px 8px', fontWeight:600, color:'#6A1B9A' }}>{p.projet}</td>
                        <td style={{ padding:'5px 8px', fontSize:10, color:'#888' }}>{p.bailleur || '—'}</td>
                        <td style={{ padding:'5px 8px', textAlign:'right', fontWeight:700 }}>{p.total}</td>
                        <td style={{ padding:'5px 8px', textAlign:'right', fontWeight:700, color:'#00897B' }}>
                          {parseFloat(p.etp||0).toFixed(2)}
                        </td>
                        <td style={{ padding:'5px 8px', textAlign:'right', color:'#e91e8c' }}>{p.femmes}</td>
                        <td style={{ padding:'5px 8px', textAlign:'right', color:'#1565C0' }}>{p.hommes}</td>
                        <td style={{ padding:'5px 8px', textAlign:'right', color:'#888', fontSize:11 }}>{pct}%</td>
                      </tr>
                    );
                  });
                })()}
                {indicateurs_projet.length > 0 && (
                  <tr style={{ background:'#f3e5f5', borderTop:'2px solid #6A1B9A', fontWeight:700 }}>
                    <td style={{ padding:'6px 8px', color:'#6A1B9A' }}>Total</td>
                    <td style={{ padding:'6px 8px', fontSize:10, color:'#888' }}>—</td>
                    <td style={{ padding:'6px 8px', textAlign:'right', color:'#6A1B9A' }}
                        title="Nombre de personnes distinctes : un agent réparti sur plusieurs projets n'est compté qu'une fois.">
                      {projetTotaux
                        ? projetTotaux.employes_distincts
                        : indicateurs_projet.reduce((s,p) => s + parseInt(p.total||0), 0)}
                    </td>
                    <td style={{ padding:'6px 8px', textAlign:'right', color:'#00897B' }}>
                      {(projetTotaux
                        ? projetTotaux.etp
                        : indicateurs_projet.reduce((s,p) => s + parseFloat(p.etp||0), 0)).toFixed(2)}
                    </td>
                    <td style={{ padding:'6px 8px', textAlign:'right', color:'#e91e8c' }}>
                      {indicateurs_projet.reduce((s,p) => s + parseInt(p.femmes||0), 0)}
                    </td>
                    <td style={{ padding:'6px 8px', textAlign:'right', color:'#1565C0' }}>
                      {indicateurs_projet.reduce((s,p) => s + parseInt(p.hommes||0), 0)}
                    </td>
                    <td style={{ padding:'6px 8px', textAlign:'right', color:'#888', fontSize:11 }}>100%</td>
                  </tr>
                )}
              </tbody>
            </table>
            {projetTotaux && (
              <div style={{ fontSize:10.5, color:'#888', marginTop:6, lineHeight:1.5 }}>
                {projetTotaux.affectations} affectation{projetTotaux.affectations > 1 ? 's' : ''} pour{' '}
                <strong>{projetTotaux.employes_distincts} agents</strong> — la somme des lignes dépasse
                l'effectif car un agent peut servir plusieurs projets. Seul l'<strong>ETP</strong>
                {' '}est additif : son total ({projetTotaux.etp.toFixed(2)}) correspond à l'effectif réel.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Taux de rotation détail + Ventilation projet/bailleur ─────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 14, marginBottom: 14 }}>

        {/* Taux de rotation */}
        <div className="card" style={{ padding: 14 }}>
          <div className="card-title">Taux de rotation du personnel</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 32, fontWeight: 800, color: tauxRotation !== null && tauxRotation > 15 ? '#e53935' : '#1565C0' }}>
              {tauxRotation !== null ? tauxRotation.toFixed(1) + '%' : 'N/A'}
            </span>
            {rotationEffMoyen !== null && (
              <span style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>
                effectif moyen&nbsp;: {rotationEffMoyen}
              </span>
            )}
          </div>
          {rotationDetailMotifs.length > 0 ? (
            <>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 600 }}>Détail des départs</div>
              {rotationDetailMotifs.map((m, i) => {
                const totalDeparts = rotationDetailMotifs.reduce((s, x) => s + parseInt(x.total || 0), 0);
                const pct = totalDeparts ? Math.round(parseInt(m.total || 0) / totalDeparts * 100) : 0;
                const colors = ['#e53935','#E65100','#6A1B9A','#00838F','#1565C0','#2E7D32'];
                return (
                  <div key={i} style={{ marginBottom: 7 }}>
                    <div className="progress-label">
                      <span style={{ fontSize: 11 }}>{m.motif || '—'}</span>
                      <span style={{ fontWeight: 700, fontSize: 11 }}>{m.total} ({pct}%)</span>
                    </div>
                    <div className="perf-bar-wrap">
                      <div className="perf-bar" style={{ width: pct + '%', background: colors[i % colors.length] }} />
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <div className="empty" style={{ padding: '0.5rem 0', fontSize: 12 }}>
              {tauxRotation !== null ? 'Aucun départ enregistré cette année' : 'Données non disponibles'}
            </div>
          )}
        </div>

        {/* Ventilation projet / bailleur */}
        <div className="card" style={{ padding: 14 }}>
          <div className="card-title">Ventilation masse salariale — Projet / Bailleur</div>
          {ventilation_projet_bailleur.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Projet</th>
                    <th>Bailleur</th>
                    <th style={{ textAlign: 'center' }}>Agents</th>
                    <th style={{ textAlign: 'center', color: '#00897B' }}
                        title="Équivalent temps plein — seule mesure additive">ETP</th>
                    <th style={{ textAlign: 'right' }}>Masse nette (FCFA)</th>
                    <th style={{ textAlign: 'right' }}>Masse brute (FCFA)</th>
                  </tr>
                </thead>
                <tbody>
                  {ventilation_projet_bailleur.map((v, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600, fontSize: 12 }}>{v.projet || '—'}</td>
                      <td style={{ fontSize: 12 }}>
                        <span className="badge badge-blue" style={{ fontSize: 10 }}>{v.bailleur || v.budget_code || '—'}</span>
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 700 }}>{v.nb_agents ?? '—'}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: '#00897B', fontSize: 12 }}>
                        {v.etp != null ? parseFloat(v.etp).toFixed(2) : '—'}
                      </td>
                      <td style={{ textAlign: 'right', fontSize: 12 }}>{fmt(v.masse_nette)}</td>
                      <td style={{ textAlign: 'right', fontSize: 12, color: '#888' }}>{fmt(v.masse_brute)}</td>
                    </tr>
                  ))}
                  {/* Ligne total */}
                  <tr style={{ background: '#f0f4fa', fontWeight: 700 }}>
                    <td colSpan={2} style={{ fontSize: 12 }}>Total</td>
                    <td style={{ textAlign: 'center' }}
                        title="Agents payés distincts sur le mois — un agent réparti sur plusieurs projets n'est compté qu'une fois.">
                      {ventilationTotaux
                        ? ventilationTotaux.agents_distincts
                        : ventilation_projet_bailleur.reduce((s, v) => s + parseInt(v.nb_agents || 0), 0)}
                    </td>
                    <td style={{ textAlign: 'center', color: '#00897B' }}>
                      {ventilation_projet_bailleur
                        .reduce((s, v) => s + parseFloat(v.etp || 0), 0).toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'right', fontSize: 12, color: '#1565C0' }}>
                      {fmt(ventilation_projet_bailleur.reduce((s, v) => s + parseFloat(v.masse_nette || 0), 0))}
                    </td>
                    <td style={{ textAlign: 'right', fontSize: 12, color: '#888' }}>
                      {fmt(ventilation_projet_bailleur.reduce((s, v) => s + parseFloat(v.masse_brute || 0), 0))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty" style={{ padding: '1rem' }}>Aucune ventilation disponible pour cette période</div>
          )}
        </div>
      </div>

      {/* ── Récapitulatif paie ────────────────────────────────────────────── */}
      <div className="card">
        <div className="card-title">Récapitulatif paie — {MOIS[now.getMonth()]} {now.getFullYear()}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
          {[
            { l: 'Bulletins générés',      v: kpi?.bulletins_mois ?? 0,       c: 'mv-blue'   },
            { l: 'Net total versé (FCFA)',  v: fmt(kpi?.masse_salariale),       c: 'mv-green'  },
            { l: 'Congés en attente',       v: kpi?.conges_en_attente ?? 0,    c: 'mv-orange' },
            { l: 'Employés actifs',         v: kpi?.total_employes ?? 0,        c: 'mv-blue'   },
          ].map((m, i) => (
            <div key={i} className="metric">
              <div className="metric-label">{m.l}</div>
              <div className={`metric-value ${m.c}`} style={{ fontSize: 20 }}>{m.v}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}